/**
 * The config surface the promotion engine actually reads.
 *
 * cinnamon passes its whole 498-line `IConfig` — which transitively pulls in
 * MUI component prop types — into every promotion helper. Auditing the ported
 * code showed the domain only ever touches a dozen `options` flags and the
 * `texts` library, so that is all this type exposes. Keeping it narrow is what
 * lets `core` stay free of the UI library.
 *
 * The authorable production config is the zod schema in `@commerce/tokens`;
 * this is the read-side view the domain needs.
 */
export interface PromotionOptions {
  currency?: string
  currencySymbol?: string
  currencyCode?: string
  currencyPrefix?: string
  currencyDisplay?: string
  format?: string
  promotionPercentageFormat?: string
  freegoodAsCartGroup?: boolean
  freegoodsAsSubitem?: boolean
  /**
   * Authoring side of hide-prices: which customer custom field flags a
   * price-hidden customer, and the value that means "hide".
   */
  hidePrices?: HidePricesConfig
  /**
   * Read side of hide-prices: whether prices are hidden for *this* customer.
   * Derived by the config slice from `hidePrices` plus the session customer;
   * the promotion engine and the view-models only ever read this flag.
   */
  hidePricesActive?: boolean
  productDetailView?: boolean
  showFreegoodsComboOrigin?: boolean
  splitPromotionTagCatalog?: boolean
}

/**
 * Hide-prices is a per-customer feature: a storefront enables it and names the
 * customer custom field plus the value that marks a customer as price-hidden.
 * Enabled without both of those is a misconfiguration, not a "hide everything"
 * instruction — see `resolveHidePrices`.
 */
export interface HidePricesConfig {
  enabled?: boolean
  customField?: string
  flagValue?: string | number | boolean
}

/**
 * Copy library: namespace -> key -> string.
 *
 * The index signature deliberately returns a non-optional record, matching
 * cinnamon's original `ITextLibrary`. Adding `| undefined` here is what broke
 * the port: the ported helpers read `config.texts.promotions.someKey`
 * directly, so every such access became a possibly-undefined error.
 */
export interface ITextLibrary {
  [namespace: string]: {
    [key: string]: string
  }
}

/** Alias kept for readability at call sites in new code. */
export type PromotionTextLibrary = ITextLibrary

/**
 * `texts` is required, as it is in cinnamon. A storefront with no copy is not
 * a case the promotion engine was written to handle, and making it optional
 * only pushes the failure into every message-formatting helper.
 */
export interface PromotionConfig {
  options?: PromotionOptions
  texts: ITextLibrary
}

/**
 * Alias so the ported promotion helpers keep compiling under the name they
 * were written against. New code should reference `PromotionConfig`.
 */
export type IConfig = PromotionConfig

export enum Localization {
  En = 'en',
  Es = 'es',
  Pt = 'pt',
  Zh = 'zh',
  Ar = 'ar',
  He = 'he',
  Th = 'th',
  EnUs = 'en-us',
  EsMx = 'es-mx',
  PtBr = 'pt-br',
  ZhCn = 'zh-cn',
}

/** Cart-level blocking conditions the backend reports. */
export enum AlertConstrains {
  cartConditionPendingMinQty = 'cartConditionPendingMinQty',
  cartConditionPendingMinAmount = 'cartConditionPendingMinAmount',
  cartConditionPendingMaxAmount = 'cartConditionPendingMaxAmount',
  cartConditionPendingMaxQty = 'cartConditionPendingMaxQty',
  creditLimitMaxAmount = 'creditLimitMaxAmount',
}

export type IAlertsStatus = Record<AlertConstrains, { isActive: boolean }>

export enum SessionNotifications {
  STOCK_AVAILABLE = 'STOCK_AVAILABLE',
  AUTHENTICATION_NEEDED = 'AUTHENTICATION_NEEDED',
  SESSION_CREATED = 'SESSION_CREATED',
  ORDER_REMINDER = 'ORDER_REMINDER',
  ABANDONED_CART = 'ABANDONED_CART',
}

export type SessionNotificationsArray = Array<{
  type: SessionNotifications
  enabled: boolean
}>
